import { configureStore, createSlice } from '@reduxjs/toolkit'
import { getFromStorage } from '../utils/storage'
import storeReducer from './slices/storeSlice'

const currentUser = getFromStorage('current-user')

const initialState = {
	currentUser: {
		isLoggedIn: currentUser !== null ? true : false,
		id: currentUser?.id,
		name: currentUser?.name,
		email: currentUser?.email,
		photo: currentUser?.photo,
		store: currentUser?.store,
		storeName: currentUser?.storeName,
		role: currentUser?.role,
		roleDescription: currentUser?.roleDescription,
		permission: currentUser?.permission,
	},
}

const currentUserSlice = createSlice({
	name: 'current user',
	initialState,
	reducers: {
		isLogin(state, action) {
			state.currentUser = {
				isLoggedIn: true,
				id: action.payload.id,
				name: action.payload.name,
				email: action.payload.email,
				photo: action.payload.photo,
				store: action.payload.store,
				storeName: action.payload.storeName,
				role: action.payload.role,
				roleDescription: action.payload.roleDescription,
				permission: action.payload.permission,
			}
		},
		// Admin/director/assistant working a store's board act as a member of that store
		actAsStore(state, action) {
			state.currentUser.store = action.payload.store
			state.currentUser.storeName = action.payload.storeName
		},
		stopActingAsStore(state) {
			const savedUser = getFromStorage('current-user')
			state.currentUser.store = savedUser?.store
			state.currentUser.storeName = savedUser?.storeName
		},
		isLogout(state) {
			state.currentUser = {
				isLoggedIn: false,
				id: '',
				name: '',
				email: '',
				photo: '',
				store: '',
				storeName: '',
				role: '',
				roleDescription: '',
				permission: [],
			}
		},
	},
})

const isLoading = { isLoading: true }

const loadingSlice = createSlice({
	name: 'Loading',
	initialState: isLoading,
	reducers: {
		isLoading(state) {
			state.isLoading = true
		},
		isLoaded(state) {
			state.isLoading = false
		},
	},
})

const updateForm = { showUpdateForm: false, editOrder: {} }

const updateFormSlice = createSlice({
	name: 'update form',
	initialState: updateForm,
	reducers: {
		showUpdateForm(state) {
			state.showUpdateForm = true
		},
		hideUpdateForm(state) {
			state.showUpdateForm = false
		},
		editOrder(state, action) {
			state.editOrder = action.payload
		},
	},
})

const filterByTag = { showFilterByTag: false, tagList: [], adminStore: '' }

const filterByTagSlice = createSlice({
	name: 'filter by tag',
	initialState: filterByTag,
	reducers: {
		showFilterByTag(state) {
			state.showFilterByTag = true
		},
		hideFilterByTag(state) {
			state.showFilterByTag = false
		},
		toggleFilterByTag(state) {
			state.showFilterByTag = !state.showFilterByTag
		},
		getTagList(state, action) {
			state.tagList = action.payload.map((tag) => {
				return { ...tag, check: false }
			})
		},
		checkTag(state, action) {
			const index = action.payload
			const updateTagList = JSON.parse(JSON.stringify(state.tagList))
			const findTagIndex = updateTagList.findIndex((tag) => tag.index === index)
			updateTagList[findTagIndex].check = !updateTagList[findTagIndex].check
			state.tagList = updateTagList
		},
		unCheckAll(state) {
			const updateTagList = JSON.parse(JSON.stringify(state.tagList)).map((tag) => {
				return { ...tag, check: false }
			})
			state.tagList = updateTagList
		},
		getAdminStore(state, action) {
			state.adminStore = action.payload
		},
	},
})

const updateScheduleForm = { showUpdateScheduleForm: false, editOrder: {} }

const updateScheduleFormSlice = createSlice({
	name: 'update schedule form',
	initialState: updateScheduleForm,
	reducers: {
		showUpdateScheduleForm(state) {
			state.showUpdateScheduleForm = true
		},
		hideUpdateScheduleForm(state) {
			state.showUpdateScheduleForm = false
		},
		editOrder(state, action) {
			state.editOrder = action.payload
		},
	},
})

const importExportStockForm = { showForm: false, product: {} }

const importExportExportStockFormSlice = createSlice({
	name: 'import export form',
	initialState: importExportStockForm,
	reducers: {
		showImportExportStockForm(state) {
			state.showForm = true
		},
		hideImportExportStockForm(state) {
			state.showForm = false
		},
		product(state, action) {
			state.product = action.payload
		},
	},
})

const stockParamsForm = { showParamsForm: false, stockParams: {} }
const stockParamsFormSlice = createSlice({
	name: 'stock params form',
	initialState: stockParamsForm,
	reducers: {
		showStockParamsForm(state) {
			state.showParamsForm = true
		},
		hideStockParamsForm(state) {
			state.showParamsForm = false
		},
		stockParams(state, action) {
			state.stockParams = action.payload
		},
	},
})

const stockParamsByStoreForm = { showParamsByStoreForm: false, stockParams: {} }
const stockParamsByStoreFormSlice = createSlice({
	name: 'stock params by store form',
	initialState: stockParamsByStoreForm,
	reducers: {
		showStockParamsByStoreForm(state) {
			state.showParamsByStoreForm = true
		},
		hideStockParamsByStoreForm(state) {
			state.showParamsByStoreForm = false
		},
		stockParams(state, action) {
			state.stockParams = action.payload
		},
	},
})

const confirmImportStockByStoreForm = { showConfirmImportByStoreForm: false, request: {} }
const confirmImportStockByStoreFormSlice = createSlice({
	name: 'confirm import stock by store form',
	initialState: confirmImportStockByStoreForm,
	reducers: {
		showConfirmImportStockByStoreForm(state) {
			state.showConfirmImportByStoreForm = true
		},
		hideConfirmImportStockByStoreForm(state) {
			state.showConfirmImportByStoreForm = false
		},
		request(state, action) {
			state.request = action.payload
		},
	},
})

const billDetail = { detail: [], store: '' }
const billDetailSlice = createSlice({
	name: 'bill detail',
	initialState: billDetail,
	reducers: {
		getBillDetail(state, action) {
			state.billDetail = { detail: action.payload.detail, store: action.payload.store }
		},
	},
})

const store = configureStore({
	reducer: {
		currentUser: currentUserSlice.reducer,
		isLoading: loadingSlice.reducer,
		updateForm: updateFormSlice.reducer,
		filterByTag: filterByTagSlice.reducer,
		updateScheduleForm: updateScheduleFormSlice.reducer,
		importExportStockForm: importExportExportStockFormSlice.reducer,
		stockParamsForm: stockParamsFormSlice.reducer,
		stockParamsByStoreForm: stockParamsByStoreFormSlice.reducer,
		confirmImportStockByStoreForm: confirmImportStockByStoreFormSlice.reducer,
		billDetail: billDetailSlice.reducer,
		store: storeReducer,
	},
})

export const currentUserActions = currentUserSlice.actions
export const loadingActions = loadingSlice.actions
export const updateFormActions = updateFormSlice.actions
export const filterByTagActions = filterByTagSlice.actions
export const updateScheduleFormActions = updateScheduleFormSlice.actions
export const importExportStockFormActions = importExportExportStockFormSlice.actions
export const stockParamsFormActions = stockParamsFormSlice.actions
export const stockParamsFormByStoreActions = stockParamsByStoreFormSlice.actions
export const confirmImportStockFormByStoreActions = confirmImportStockByStoreFormSlice.actions
export const billDetailActions = billDetailSlice.actions

export default store
